import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { productFavoritesApi } from '../../services/apiService';
import LoginModal from '../LoginModal/LoginModal';
import './FavouriteButton.css';

/**
 * Reusable favourite/bookmark button for product cards.
 * Props:
 *   productId  – the product's id (number)
 *   className  – optional extra class on the button
 */
const FavouriteButton = ({ productId, className = '' }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // On mount / when user or productId changes – check favourite status
  useEffect(() => {
    if (!user?.id || !productId) return;
    let cancelled = false;
    productFavoritesApi
      .checkFavorite(user.id, productId)
      .then((res) => { if (!cancelled) setIsFavorited(res.isFavorited); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id, productId]);

  // Clear toast timer on unmount
  useEffect(() => {
    return () => { if (toastTimer.current) clearTimeout(toastTimer.current); };
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    if (!productId || isToggling) return;

    // Optimistic update – instant visual feedback
    const prevState = isFavorited;
    setIsFavorited(!prevState);
    setIsToggling(true);

    try {
      const res = await productFavoritesApi.toggle(user.id, productId);
      setIsFavorited(res.isFavorited);
      showToast(
        res.isFavorited ? 'Added to favourites' : 'Removed from favourites',
        res.isFavorited ? 'success' : 'info'
      );
    } catch (err) {
      // Roll back optimistic update
      setIsFavorited(prevState);
      showToast('Could not reach server. Please try again.', 'error');
      console.error('Error toggling product favourite:', err);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <>
      <button
        className={`fav-btn ${isFavorited ? 'fav-btn--active' : ''} ${isToggling ? 'fav-btn--toggling' : ''} ${className}`}
        onClick={handleClick}
        disabled={isToggling}
        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.636 18.7529 10.7698 18.7494C10.9036 18.7458 11.034 18.7066 11.1475 18.6356L16.75 15.1341L22.3534 18.6356C22.4669 18.7063 22.5972 18.7454 22.7309 18.7488C22.8646 18.7522 22.9967 18.7198 23.1136 18.655C23.2306 18.5902 23.3281 18.4953 23.396 18.3801C23.4639 18.2649 23.4998 18.1337 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z"
            fill={isFavorited ? '#1F396C' : '#969696'}
          />
        </svg>
      </button>

      {toast && (
        <div className={`fav-toast fav-toast--${toast.type}`}>
          <span>{toast.msg}</span>
          {toast.type === 'success' && (
            <button
              className="fav-toast-link"
              onClick={(e) => { e.stopPropagation(); navigate('/favorites-products'); }}
            >
              View All →
            </button>
          )}
        </div>
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onRegisterClick={() => {
          setShowLoginModal(false);
          navigate('/register');
        }}
      />
    </>
  );
};

export default FavouriteButton;
